import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MapPinPlus, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useHasPermission } from '@/hooks/usePermissions';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { jobKeys, schedulerKeys, serviceAreaKeys } from '@/lib/queryKeys';
import { COLLAPSE_MS } from './collapse';

interface Props {
  /** Fired once the fix lands, so the row that reported it can animate out. */
  onResolved?: () => void;
  technicianId: string;
  technicianName?: string;
  zipCode: string;
}

export function AddServiceAreaButton(props: Props) {
  const canEdit = useHasPermission('technicians:edit');
  return canEdit ? (
    <ServiceAreaAssignmentButton key={`${props.technicianId}-${props.zipCode}`} {...props} />
  ) : null;
}

function ServiceAreaAssignmentButton({
  technicianId,
  zipCode,
  technicianName = 'technician',
  onResolved,
}: Props) {
  const queryClient = useQueryClient();
  const add = useApiMutation<unknown>({
    mutationFn: () =>
      apiPost(operationsClient, schedulerEndpoints.technicianServiceAreas(technicianId), {
        zipCode,
      }),
    onSuccess: async () => {
      toast.success('ZIP code added to technician');
      // The objection this row reported is no longer true. Let it collapse
      // first, THEN re-read: the server drops it from every open visit that
      // still records it, and refetching mid-animation would snap the row away
      // instead of playing it out.
      onResolved?.();
      await new Promise((resolve) => setTimeout(resolve, COLLAPSE_MS));
      // A ZIP outside the company's coverage is added to it on the way, so the
      // service-area lists are re-read too.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: schedulerKeys.all() }),
        queryClient.invalidateQueries({ queryKey: jobKeys.all() }),
        queryClient.invalidateQueries({ queryKey: serviceAreaKeys.all() }),
      ]);
    },
  });

  return (
    <span className="mt-2 block">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-2 rounded-md border-current/20 bg-background/80 px-3 text-xs font-medium text-inherit shadow-none hover:bg-background"
        disabled={add.isPending || add.isSuccess}
        onClick={(event) => {
          event.stopPropagation();
          add.mutate();
        }}
      >
        {add.isPending ? (
          <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
        ) : add.isSuccess ? (
          <Check aria-hidden="true" className="size-3.5" />
        ) : (
          <MapPinPlus aria-hidden="true" className="size-3.5" />
        )}
        {add.isPending
          ? 'Adding ZIP code…'
          : add.isSuccess
            ? 'ZIP code added'
            : `Add Zip Code to ${technicianName}`}
      </Button>
    </span>
  );
}
